// Get Mapped Values
import { StringKeyValue } from '../actions/types';
import { Assets } from '../assets';
import { ContextReducer } from '../reducers/context';
import ApplicationStatus from './mappings/applicationStatus';
import ApplicationStatusName from './mappings/applicationStatusName';
import LoanCommitmentProductName from './mappings/loanCommitmentProductName';
import LoanProduct from './mappings/loanProduct';
import LoanProductName from './mappings/loanProductName';
import LoanType from './mappings/loanType';
import ProductFontIcon from './mappings/productFontIcon';

/* Product URL */
export const getProductUrl = (
    advisorApplication: boolean,
    lendoApplication: boolean,
    loanType: string,
    data: ContextReducer['config']['data'],
) => {
    const {
        completionPathSecuredLoan,
        completionPathCreditCard,
        completionPathCreditLoan,
        completionPathCreditLoanAdvisor,
    } = data;

    let creditLoanPath = completionPathCreditLoan;
    if (advisorApplication) {
        creditLoanPath = completionPathCreditLoanAdvisor;
    }
    return (
        {
            [LoanType.vehicleLoan]: completionPathSecuredLoan,
            [LoanType.loanCommitment]: completionPathSecuredLoan,
            [LoanType.creditCard]: completionPathCreditCard,
            [LoanType.creditLoan]: creditLoanPath,
        }[loanType] ?? '/'
    );
};

export const getApplicationStatus = (applicationStatus: string) => {
    const statuses = {
        [ApplicationStatus.newCase]: ApplicationStatusName.newCase,
        [ApplicationStatus.approved]: ApplicationStatusName.approved,
        [ApplicationStatus.control]: ApplicationStatusName.control,
        [ApplicationStatus.rejected]: ApplicationStatusName.rejected,
        [ApplicationStatus.documentsSent]: ApplicationStatusName.documentsSent,
        [ApplicationStatus.documentsComplete]: ApplicationStatusName.documentsComplete,
    };
    return statuses[applicationStatus] || '';
};

/* Get Product Name */
export const getProductName = (loanType: string | null | undefined, loanProduct: string): string => {
    if (loanType === LoanType.vehicleLoan) {
        return (
            {
                [LoanProduct.car]: LoanProductName.car,
                [LoanProduct.carElectric]: LoanProductName.carElectric,
                [LoanProduct.carNoInsurance]: LoanProductName.carNoInsurance,
                [LoanProduct.mc]: LoanProductName.mc,
                [LoanProduct.atv]: LoanProductName.atv,
                [LoanProduct.snowmobile]: LoanProductName.snowmobile,
                [LoanProduct.campingCar]: LoanProductName.campingCar,
                [LoanProduct.campingCaravan]: LoanProductName.campingCaravan,
                [LoanProduct.boat]: LoanProductName.boat,
            }[loanProduct] ?? LoanProductName.unknown
        );
    }
    if (loanType === LoanType.loanCommitment) {
        return (
            {
                [LoanProduct.car]: LoanCommitmentProductName.car,
                [LoanProduct.carElectric]: LoanCommitmentProductName.carElectric,
                [LoanProduct.carNoInsurance]: LoanCommitmentProductName.carNoInsurance,
                [LoanProduct.mc]: LoanCommitmentProductName.mc,
                [LoanProduct.atv]: LoanCommitmentProductName.atv,
                [LoanProduct.snowmobile]: LoanCommitmentProductName.snowmobile,
                [LoanProduct.campingCar]: LoanCommitmentProductName.campingCar,
                [LoanProduct.campingCaravan]: LoanCommitmentProductName.campingCaravan,
                [LoanProduct.boat]: LoanCommitmentProductName.boat,
            }[loanProduct] ?? LoanProductName.unknown
        );
    }
    if (loanType === LoanType.creditLoan) return LoanProductName.creditLoan;
    if (loanType === LoanType.creditCard) return LoanProductName.creditCard;
    return LoanProductName.unknown;
};

/* Get Product Title */
export const getProductApplicationTitle = (
    loanType: string | null | undefined,
    loanProduct: string,
    text: StringKeyValue,
): string => {
    const { applicationTitleA, applicationTitleB } = text;
    if (applicationTitleA && applicationTitleB && loanType && loanProduct) {
        return `${applicationTitleA} ${getProductName(loanType, loanProduct)} ${applicationTitleB}`;
    }
    return '';
};
export const getProductCompletionTitle = (
    loanType: string | null | undefined,
    loanProduct: string,
    text: StringKeyValue,
): string => {
    const { completionTitle } = text;
    if (completionTitle && loanType && loanProduct) {
        return `${completionTitle} ${getProductName(loanType, loanProduct)}`;
    }
    return '';
};
export const getProductCompletionIngress = (
    loanType: string | null | undefined,
    loanProduct: string,
    text: StringKeyValue,
): string => {
    const { completionDetailsIngressA, completionDetailsIngressB } = text;
    if (completionDetailsIngressA && completionDetailsIngressB && loanType && loanProduct) {
        return `${completionDetailsIngressA} ${getProductName(loanType, loanProduct)} ${completionDetailsIngressB}`;
    }
    return '';
};

/* Get Product Description */
export const getProductDescription = (loanProduct: string, text: StringKeyValue): string =>
    ({
        [LoanProduct.car]: text.calculatorLoanInfoDescriptionCar,
        [LoanProduct.carElectric]: text.calculatorLoanInfoDescriptionCarElectric,
        [LoanProduct.carNoInsurance]: text.calculatorLoanInfoDescriptionCarNoInsurance,
        [LoanProduct.mc]: text.calculatorLoanInfoDescriptionMC,
        [LoanProduct.atv]: text.calculatorLoanInfoDescriptionATV,
        [LoanProduct.snowmobile]: text.calculatorLoanInfoDescriptionSnowmobile,
        [LoanProduct.campingCar]: text.calculatorLoanInfoDescriptionCampingCar,
        [LoanProduct.campingCaravan]: text.calculatorLoanInfoDescriptionCampingCaravan,
        [LoanProduct.boat]: text.calculatorLoanInfoDescriptionBoat,
    })[loanProduct] ?? '';

/* Product Icon */
export const getProductIcon = (loanType: string | null | undefined, loanProduct: string) => {
    let iconName: Assets = 'SecuredLoanOther';
    if (loanType === LoanType.vehicleLoan || loanType === LoanType.loanCommitment) {
        // @ts-ignore
        iconName =
            {
                [LoanProduct.car]: 'SecuredLoanCar',
                [LoanProduct.carElectric]: 'SecuredLoanCarElectric',
                [LoanProduct.carNoInsurance]: 'SecuredLoanCarNoInsurance',
                [LoanProduct.mc]: 'SecuredLoanMC',
                [LoanProduct.atv]: 'SecuredLoanATV',
                [LoanProduct.snowmobile]: 'SecuredLoanSnowmobile',
                [LoanProduct.campingCar]: 'SecuredLoanCampingCar',
                [LoanProduct.campingCaravan]: 'SecuredLoanCampingCaravan',
                [LoanProduct.boat]: 'SecuredLoanBoat',
            }[loanProduct] ?? 'SecuredLoanOther';
    }
    if (loanType === LoanType.creditLoan) iconName = 'CreditLoan';
    if (loanType === LoanType.creditCard) iconName = 'CreditCard';
    return iconName;
};

/* Product Font Icon */
export const getProductFontIcon = (loanType: string, loanProduct: string) => {
    if (loanType === LoanType.vehicleLoan || loanType === LoanType.loanCommitment) {
        return (
            {
                [LoanProduct.car]: ProductFontIcon.car,
                [LoanProduct.carElectric]: ProductFontIcon.carElectric,
                [LoanProduct.carNoInsurance]: ProductFontIcon.carNoInsurance,
                [LoanProduct.mc]: ProductFontIcon.mc,
                [LoanProduct.atv]: ProductFontIcon.atv,
                [LoanProduct.snowmobile]: ProductFontIcon.snowmobile,
                [LoanProduct.campingCar]: ProductFontIcon.campingCar,
                [LoanProduct.campingCaravan]: ProductFontIcon.campingCaravan,
                [LoanProduct.boat]: ProductFontIcon.boat,
            }[loanProduct] ?? ProductFontIcon.unknown
        );
    }
    if (loanType === LoanType.creditLoan) return ProductFontIcon.creditLoan;
    if (loanType === LoanType.creditCard) return ProductFontIcon.creditCard;
    return ProductFontIcon.unknown;
};
